import "../../css/blocks/contact-form.css";
import type {
  ContactFormData,
  ContactFormElements,
} from "../types/contact-form";
import { FormValidator } from "../modules/form-validator";
import { FormUI } from "../modules/form-ui";
import { FormSubmitter } from "../modules/form-submitter";

class ContactFormHandler {
  private form: HTMLFormElement;
  private elements: ContactFormElements;
  private isSubmitting = false;
  private readonly validator = new FormValidator();
  private readonly ui = new FormUI();
  private readonly submitter = new FormSubmitter();

  constructor(form: HTMLFormElement) {
    this.form = form;
    this.elements = this.getFormElements();
    this.init();
  }

  private init(): void {
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
    this.addFieldValidation();
    this.ui.storeOriginalButtonText(this.elements.submitButton);
  }

  private getFormElements(): ContactFormElements {
    const formId = this.form.id;
    const fields: ContactFormElements["fields"] = {};

    // Get all form fields
    [
      "first-name",
      "last-name",
      "company",
      "address",
      "postal-code",
      "city",
      "email",
      "phone",
      "message",
      "privacy",
    ].forEach((fieldName) => {
      const element = this.form.querySelector(`#${formId}-${fieldName}`) as
        | HTMLInputElement
        | HTMLTextAreaElement;
      if (element) {
        fields[fieldName.replace("-", "_")] = element;
      }
    });

    // Handle radio buttons for salutation
    const salutationRadio = this.form.querySelector(
      'input[name="salutation"]',
    ) as HTMLInputElement;
    if (salutationRadio) {
      fields.salutation = salutationRadio;
    }

    return {
      form: this.form,
      submitButton: this.form.querySelector(".contact-form-submit"),
      messageDiv: this.form.querySelector(".form-message-container"),
      fields,
    };
  }

  private addFieldValidation(): void {
    // Email validation on blur
    this.elements.fields.email?.addEventListener("blur", () => {
      const email = this.elements.fields.email as HTMLInputElement;
      if (
        email.value.length > 0 &&
        !this.validator.validateEmail(email.value)
      ) {
        this.ui.showFieldError(email, "Please enter a valid email address");
      } else {
        this.ui.clearFieldError(email);
      }
    });

    // Character count for message
    this.elements.fields.message?.addEventListener("input", () => {
      const message = this.elements.fields.message as HTMLTextAreaElement;
      this.ui.updateCharacterCount(message, 5000);
    });

    // Clear errors on input
    Object.values(this.elements.fields).forEach((field) => {
      field?.addEventListener("input", () => {
        this.ui.clearFieldError(field);
      });
    });
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (this.isSubmitting) return;

    this.ui.clearAllErrors(this.form);

    // Validate form
    const formData = new FormData(this.form);
    const validation = this.validator.validateForm(formData);

    if (!validation.valid) {
      validation.errors.forEach((error, field) => {
        const fieldElement = this.elements.fields[field];
        if (fieldElement) {
          this.ui.showFieldError(fieldElement, error);
        }
      });
      return;
    }

    this.isSubmitting = true;
    this.ui.setLoadingState(this.form, this.elements.submitButton, true);

    try {
      const contactData = this.getFormData();
      const response = await this.submitter.submit(contactData);

      if (response.success) {
        this.handleSuccess();
      } else {
        this.ui.showMessage(
          this.elements.messageDiv,
          response.data || "An error occurred.",
          "error",
        );
      }
    } catch (error) {
      this.ui.showMessage(
        this.elements.messageDiv,
        "Network error. Please try again later.",
        "error",
      );
      console.error("Form submission error:", error);
    } finally {
      this.ui.setLoadingState(this.form, this.elements.submitButton, false);
      setTimeout(() => {
        this.isSubmitting = false;
      }, 2000);
    }
  }

  private getFormData(): ContactFormData {
    const formData = new FormData(this.form);

    return {
      salutation: (formData.get("salutation") as string) || "",
      first_name: (formData.get("first_name") as string) || "",
      last_name: (formData.get("last_name") as string) || "",
      company: (formData.get("company") as string) || "",
      address: (formData.get("address") as string) || "",
      postal_code: (formData.get("postal_code") as string) || "",
      city: (formData.get("city") as string) || "",
      email: (formData.get("email") as string) || "",
      phone: (formData.get("phone") as string) || "",
      message: (formData.get("message") as string) || "",
      privacy_accepted: !!formData.get("privacy_accepted"),
      nonce: window.rediGroupAjax?.nonce || "",
      action: "redi_contact_form_submit",
    };
  }

  private handleSuccess(): void {
    const successMessage =
      this.form.dataset.successMessage ||
      window.rediGroupAjax?.translations.success ||
      "Thank you for your message.";

    this.ui.showMessage(this.elements.messageDiv, successMessage, "success");
    this.form.reset();
    this.ui.clearAllErrors(this.form);

    // Scroll to message
    this.elements.messageDiv?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    this.submitter.trackConversion();
  }
}

// Initialize forms
function initializeContactForms(): void {
  const forms = document.querySelectorAll(
    ".contact-form",
  ) as NodeListOf<HTMLFormElement>;

  forms.forEach((form) => {
    if (!form.hasAttribute("data-initialized")) {
      new ContactFormHandler(form);
      form.setAttribute("data-initialized", "true");
    }
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeContactForms);
} else {
  initializeContactForms();
}

// Handle dynamic forms (for Gutenberg editor)
if (window.wp?.domReady) {
  window.wp.domReady(() => {
    const observer = new MutationObserver(() => {
      initializeContactForms();
    });

    const editor = document.querySelector(".block-editor-writing-flow");
    if (editor) {
      observer.observe(editor, { childList: true, subtree: true });
    }
  });
}

export { ContactFormHandler };
